import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface PaymentReviewItem {
  id: string;
  bookingCode: string;
  customerName: string;
  customer: {
    customerCode: string;
    customerType: 'INDIVIDUAL' | 'CORPORATE';
    name: string;
    taxId: string | null;
    phone: string;
    email: string | null;
    lineId: string | null;
    address: string | null;
    fileUrl: string | null;
  };
  tripName: string;
  travelDate: string;
  travelerCount: number;
  pricePerPerson: number | string;
  discount: number | string;
  bookingTotal: number | string;
  estimatedExpense: number | string;
  payerName: string;
  paymentType: string;
  amount: number | string;
  bookingAmount: number | string;
  creditCardFeePercent: number;
  creditCardFeeResponsibility: 'CUSTOMER' | 'COMPANY' | null;
  creditCardFeeAmount: number | string;
  withholdingTaxAmount: number | string;
  paidAt: string;
  paidTime: string;
  slipFileName: string;
  slipUrl: string | null;
  receiptUrl: string;
  quotationUrl: string;
  status: 'WAITING_VERIFY' | 'VERIFIED' | 'REJECTED' | 'CANCELLED';
}

@Injectable({ providedIn: 'root' })
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/payments`;

  async getWaitingReviews(): Promise<PaymentReviewItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ payments: PaymentReviewItem[] }>(`${this.apiUrl}/review`, {
        withCredentials: true,
      }),
    );
    return response.payments;
  }

  async approve(id: string, receiptUrl: string, quotationUrl: string): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.apiUrl}/review/${id}/approve`,
        { receiptUrl, quotationUrl },
        { withCredentials: true },
      ),
    );
  }

  async reject(id: string): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.apiUrl}/review/${id}/reject`, {}, { withCredentials: true }),
    );
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }
}
